import { useEffect, useMemo, type MouseEvent } from 'react';
import { marked } from 'marked';
import ttsHelpMd from './tts.md?raw';
import stsHelpMd from './sts.md?raw';
import simpleHelpMd from './simple.md?raw';
import styles from './HelpOverlay.module.css';

interface Props {
  open: boolean;
  /** 'simple' = Multi-Lingual-Projekte (simpleMode): TTS-Guide ohne Video/
   *  Mixer/Background-Audio/Multi-Voice, dafür mit Zeitlimit-Abschnitt. */
  variant: 'tts' | 'sts' | 'simple';
  /** Multi-Lingual (Spec 2026-09-21 § 6): auch im vollen `tts`/`sts`-Guide
   *  gibt es dort weder eigene Videos/Assets noch ein SFX-Bett. Die
   *  betreffenden Absätze sind in den .md-Dateien mit
   *  `<!-- own-media:start/end -->` geklammert und fallen dann raus —
   *  so bleibt der Guide-Text selbst unangetastet. */
  hideSfxAndCustom?: boolean;
  onClose(): void;
}

const OWN_MEDIA_BLOCK = /^[ \t]*<!--\s*own-media:start\s*-->[\s\S]*?<!--\s*own-media:end\s*-->[ \t]*\r?\n?/gm;

/** Entfernt die geklammerten Absätze; ohne Klammer bleibt alles, wie es ist. */
export function stripOwnMediaSections(md: string): string {
  return md.replace(OWN_MEDIA_BLOCK, '');
}

// Marked options: inline GFM (lists, code spans) is enough for these docs.
// `breaks: false` keeps single newlines as space (matches how the .md is
// authored); paragraph breaks come from blank lines.
marked.setOptions({ gfm: true, breaks: false });

export function HelpOverlay({ open, variant, hideSfxAndCustom, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Pre-render both variants once. The MD source is bundled at build-time
  // via Vite's `?raw` import — to update copy, edit the .md file and push;
  // the deploy rebuilds and the new text is live. Icons in the MD only as
  // Phosphor `<i class="ph-…">` (marked passes inline HTML through).
  const html = useMemo(() => {
    const src =
      variant === 'sts' ? stsHelpMd : variant === 'simple' ? simpleHelpMd : ttsHelpMd;
    return marked.parse(hideSfxAndCustom ? stripOwnMediaSections(src) : src) as string;
  }, [variant, hideSfxAndCustom]);

  if (!open) return null;

  const onBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={styles.backdrop}
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close help"
          type="button"
        >
          <i className="ph-bold ph-x" aria-hidden="true" />
        </button>
        <div
          className={styles.body}
          // Source is in-repo Markdown authored by the dev team (see
          // ./tts.md / ./sts.md). No user input ever flows in here, so
          // dangerouslySetInnerHTML is the right tool — bundle is fully
          // controlled.
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}
